/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import {
  Button,
  Dropdown,
  Option,
  Input,
  Checkbox,
  Textarea,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@fluentui/react-components";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { EditRegular } from "@fluentui/react-icons";
import { useMsal } from "@azure/msal-react";
import type { IProjektItem } from "../../models/IProjektItem";
import { DataService } from "../../services/dataService";
import type { IDokumentItem } from "../../models/IDokumentItem";
import type { IDataItem } from "../../models/IDataItem";
import {
  formatDate,
  formatValue,
  getStudiengangsArtText,
} from "../../utils/format";
import Dokumente from "./dokumente";
import { useGrunddatenStyles as useStyles } from "../../utils/styles";

interface GrunddatenProps {
  canEdit?: boolean;
  projekt: IProjektItem;
  dokumente: IDokumentItem[];
  onProjektUpdated: () => Promise<void>;
  onDokumenteUpdated: () => Promise<void>;
}

const Grunddaten: React.FC<GrunddatenProps> = ({
  canEdit = false,
  projekt: detailData,
  dokumente,
  onProjektUpdated,
  onDokumenteUpdated,
}) => {
  const panelTopRef = React.useRef<HTMLDivElement>(null);
  const styles = useStyles();
  const { instance, accounts } = useMsal();
  const accountInfo = accounts[0] || null;
  const dataService = React.useMemo(() => {
    return accountInfo ? new DataService(instance, accountInfo) : null;
  }, [instance, accountInfo]);
  const [errorMessage, setErrorMessage] = React.useState<string>("");
  const [color, setColor] = useState("");
  const [projektname, setProjektname] = useState("");
  const [studiengangsbezeichnung, setStudiengangsbezeichnung] = useState("");
  const [studiengangsArt, setStudiengangsArt] = useState("");
  const [institution, setInstitution] = useState("");
  const [studienfach, setStudienfach] = useState("");
  const [studiengangsstart, setStudiengangsstart] = useState<Date | undefined>(
    undefined,
  );
  const [organisationsstruktur, setOrganisationsstruktur] = useState("");
  const [gesamtumfang, setGesamtumfang] = useState(0);
  const [studiendauer, setStudiendauer] = useState(0);
  const [unterrichtssprache, setUnterrichtssprache] = useState("");
  const [akademischerGrad, setAkademischerGrad] = useState("");
  const [anzahlStudienplaetze, setAnzahlStudienplaetze] = useState(0);
  const [standort, setStandort] = useState("");
  const [qualifikationsniveau, setQualifikationsniveau] = useState("");
  const [zulassungsvoraussetzungen, setZulassungsvoraussetzungen] =
    useState("");

  const [personen, setPersonen] = useState([] as IDataItem[]);
  const [selectedPerson, setSelectedPerson] = useState<string>("");

  const [finanz, setFinanz] = useState([] as IDataItem[]);
  const [selectedPersonFinanz, setSelectedPersonFinanz] = useState<string>("");

  const [freigeber, setFreigeber] = useState([] as IDataItem[]);
  const [selectedPersonFreigeber, setSelectedPersonFreigeber] =
    useState<string>("");

  const [controlling, setControlling] = useState([] as IDataItem[]);
  const [selectedPersonControlling, setSelectedPersonControlling] =
    useState<string>("");

  const [inhalt, setInhalt] = useState([] as IDataItem[]);
  const [selectedPersonInhalt, setSelectedPersonInhalt] = useState<string>("");

  const [studiengangsleitung, setStudiengangsleitung] = useState(
    [] as IDataItem[],
  );
  const [selectedPersonStg, setSelectedPersonStg] = useState<string>("");

  const [interimistisch, setInterimistisch] = useState(false);
  const [weiterverfolgung, setWeiterverfolgung] = useState(false);

  const [editMode, setEditMode] = React.useState(false);

  const [updateBemerkung, setUpdateBemerkung] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const scrollToTopOfPanel = () => {
    setTimeout(() => {
      if (panelTopRef.current) {
        // First, use scrollIntoView immediately
        panelTopRef.current.scrollIntoView({
          behavior: "auto",
          block: "start",
        });

        // Then find and scroll the scrollable parent to ensure we're at the top
        let element = panelTopRef.current.parentElement;
        while (element) {
          const overflowY = window.getComputedStyle(element).overflowY;
          const isScrollable = overflowY === "scroll" || overflowY === "auto";
          if (isScrollable && element.scrollHeight > element.clientHeight) {
            element.scrollTop = 0;
            break;
          }
          element = element.parentElement;
        }
      }
    }, 150);
  };

  useEffect(() => {
    const getPersonen = async (art: string) => {
      if (!dataService) return;
      const response = await dataService.getRelevantePersonen(art);
      if (response) {
        if (art === "Entwickler*in") {
          setPersonen(response);
        }
        if (art === "Freigeber*in") {
          setFreigeber(response);
        }
        if (art === "Controlling") {
          setControlling(response);
        }
        if (art === "Hochschulleitung") {
          setFinanz(response);
        }
        if (art === "Kollegium") {
          setInhalt(response);
        }
        if (art === "Studiengangsleiter*in") {
          setStudiengangsleitung(response);
        }
      }
    };
    getPersonen("Entwickler*in");
    getPersonen("Freigeber*in");
    getPersonen("Controlling");
    getPersonen("Hochschulleitung");
    getPersonen("Kollegium");
    getPersonen("Studiengangsleiter*in");
  }, [dataService]);

  const setEditFields = React.useCallback(() => {
    if (!detailData) return;

    setProjektname(detailData.bezeichnung ?? "");
    setStudiengangsbezeichnung(detailData.studiengang ?? "");
    setStudiengangsArt(detailData.studiengangsArt?.toString() ?? "");
    setInstitution(detailData.institution ?? "");
    setStudienfach(detailData.studienfach ?? "");
    setStudiengangsstart(detailData.studiengangsstart ?? undefined);
    setOrganisationsstruktur(detailData.organisationsstruktur ?? "");
    setGesamtumfang(detailData.ects ?? 0);
    setStudiendauer(detailData.studiendauer ?? 0);
    setUnterrichtssprache(detailData.unterrichtssprache ?? "");
    setAkademischerGrad(detailData.akadGrad ?? "");
    setAnzahlStudienplaetze(detailData.studienplaetze ?? 0);
    setStandort(detailData.standort ?? "");
    setQualifikationsniveau(detailData.qualifikationsniveau ?? "");
    setZulassungsvoraussetzungen(detailData.zulassungsvoraussetzungen ?? "");
    setSelectedPerson(detailData.entwicklerUpn ?? "");
    setSelectedPersonFinanz(detailData.finanzFreigabeUpn ?? "");
    setSelectedPersonFreigeber(detailData.freigabeUpn ?? "");
    setSelectedPersonControlling(detailData.controllingUpn ?? "");
    setSelectedPersonInhalt(detailData.inhaltFreigabeUpn ?? "");
    setSelectedPersonStg(detailData.studiengangsleitungUpn ?? "");
    setInterimistisch(Boolean(detailData.interimistisch));
    setWeiterverfolgung(detailData.statusId === 2);
  }, [detailData]);

  const checkRequiredFields = () => {
    let result = false;
    result =
      projektname.trim() === "" ||
      studiengangsbezeichnung.trim() === "" ||
      studiengangsArt === "" ||
      institution.trim() === "" ||
      studienfach.trim() === "" ||
      studiengangsstart === undefined ||
      organisationsstruktur.trim() === "" ||
      gesamtumfang === 0 ||
      studiengangsstart === undefined ||
      unterrichtssprache.trim() === "" ||
      akademischerGrad.trim() === "" ||
      anzahlStudienplaetze === 0 ||
      standort.trim() === "" ||
      qualifikationsniveau.trim() === "" ||
      zulassungsvoraussetzungen.trim() === "" ||
      selectedPerson.trim() === "" ||
      selectedPersonFreigeber.trim() === "" ||
      selectedPersonInhalt.trim() === "" ||
      selectedPersonFinanz.trim() === "" ||
      selectedPersonControlling.trim() === "" ||
      selectedPersonStg.trim() === "";
    return result;
  };

  const saveProjekt = async () => {
    setErrorMessage("");
    if (!dataService) return;
    if (checkRequiredFields()) {
      setErrorMessage("Bitte füllen Sie alle erforderlichen Felder aus.");
      setColor("red");
      scrollToTopOfPanel();
      return;
    }
    await dataService
      .addUpdateProjekt({
        itemId: detailData.itemId,
        bezeichnung: projektname,
        studiengang: studiengangsbezeichnung,
        studiengangsArt: Number(studiengangsArt),
        institution: institution,
        studienfach: studienfach,
        studiengangsstart: studiengangsstart,
        organisationsstruktur: organisationsstruktur,
        ects: gesamtumfang,
        unterrichtssprache: unterrichtssprache,
        akadGrad: akademischerGrad,
        studienplaetze: anzahlStudienplaetze,
        standort: standort,
        qualifikationsniveau: qualifikationsniveau,
        zulassungsvoraussetzungen: zulassungsvoraussetzungen,
        studiendauer: studiendauer,
        entwicklerUpn: selectedPerson,
        freigabeUpn: selectedPersonFreigeber,
        inhaltFreigabeUpn: selectedPersonInhalt,
        finanzFreigabeUpn: selectedPersonFinanz,
        controllingUpn: selectedPersonControlling,
        studiengangsleitungUpn: selectedPersonStg,
        interimistisch: interimistisch,
        statusId: weiterverfolgung
          ? 2
          : detailData?.statusId !== 2
            ? detailData?.statusId
            : 1,
        updateBemerkung: updateBemerkung,
        kategorie: "Grunddaten & relevante Personen",
      } as IProjektItem)
      .then(async (_result) => {
        await onProjektUpdated();
        setUpdateBemerkung("");
        setErrorMessage("Daten erfolgreich gespeichert");
        setColor("green");
        scrollToTopOfPanel();
        setTimeout(() => {
          setErrorMessage("");
          setEditMode(false);
        }, 2000);
      })
      .catch((error) => {
        console.log(error);
        setErrorMessage("Fehler beim Speichern der Projektdaten");
        setColor("red");
        scrollToTopOfPanel();
      });
  };

  const _saveClicked = () => {
    setIsDialogOpen(true);
  };

  const renderReadMode = () => (
    <>
      <div className="verticalLayout">
        <section className={styles.section}>
          <div className={styles.headerRow}>
            <h3 className={styles.sectionTitle}>Grunddaten</h3>
            {canEdit && (
              <Button
                appearance="secondary"
                icon={<EditRegular />}
                onClick={() => {
                  setEditFields();
                  setEditMode(true);
                }}
              >
                Bearbeitungsmodus starten
              </Button>
            )}
          </div>
          <div className={styles.gridContainer}>
            <div className={styles.label} style={{ gridColumn: "1" }}>
              Projektname:
            </div>
            <div className={styles.valueSingle}>
              {formatValue(detailData.bezeichnung)}
            </div>
            <div className={styles.valueWeiter}>
              {detailData.statusId === 2
                ? "Projekt wird nicht weiterverfolgt"
                : ""}
            </div>
            <div className={styles.label}>Studiengangsbezeichnung:</div>
            <div className={styles.value}>
              {formatValue(detailData.studiengang)}
            </div>
            <div className={styles.label}>Studiengangsart:</div>
            <div className={styles.value}>
              {getStudiengangsArtText(detailData.studiengangsArt)}
            </div>

            <div className={styles.label}>Unterrichtssprache:</div>
            <div className={styles.value}>
              {formatValue(detailData.unterrichtssprache)}
            </div>

            <div className={styles.label}>Institution:</div>
            <div className={styles.value}>
              {formatValue(detailData.institution)}
            </div>

            <div className={styles.label}>
              Akademischer Grad (inkl. Abkürzung):
            </div>
            <div className={styles.value}>
              {formatValue(detailData.akadGrad)}
            </div>

            <div className={styles.label}>Studienfach:</div>
            <div className={styles.value}>
              {formatValue(detailData.studienfach)}
            </div>

            <div className={styles.label}>Studiengangsleitung:</div>
            <div className={styles.value}>
              {formatValue(detailData.studiengangsleitungName)}
            </div>

            <div className={styles.label}>Studiengangsstart:</div>
            <div className={styles.value}>
              {formatDate(detailData.studiengangsstart)}
            </div>

            <div className={styles.label}>Anzahl der Studienplätze:</div>
            <div className={styles.value}>
              {formatValue(detailData.studienplaetze)}
            </div>

            <div className={styles.label}>Organisationsstruktur:</div>
            <div className={styles.value}>
              {formatValue(detailData.organisationsstruktur)}
            </div>

            <div className={styles.label}>Programmstandort:</div>
            <div className={styles.value}>
              {formatValue(detailData.standort)}
            </div>

            <div className={styles.label}>Gesamtumfang in ECTS-Punkten:</div>
            <div className={styles.value}>{formatValue(detailData.ects)}</div>

            <div className={styles.label}>Qualifikationsniveau:</div>
            <div className={styles.value}>
              {formatValue(detailData.qualifikationsniveau)}
            </div>

            <div className={styles.label}>Studiendauer in Semestern:</div>
            <div className={styles.value}>
              {formatValue(detailData.studiendauer)}
            </div>

            <div className={styles.label}>Zulassungsvoraussetzungen:</div>
            <div className={styles.value}>
              {formatValue(detailData.zulassungsvoraussetzungen)}
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.headerRow}>
            <h3 className={styles.sectionTitle}>Relevante Personen</h3>
          </div>
          <div className={styles.gridContainer}>
            <div className={styles.label}>Entwickler*in:</div>
            <div className={styles.value}>
              {formatValue(detailData.entwicklerName)}
            </div>
            <div className={styles.label}>Freigeber*in:</div>
            <div className={styles.value}>
              {formatValue(detailData.freigabeName)}
            </div>
            <div className={styles.label}>Inhaltliche Freigabe*r:</div>
            <div className={styles.value}>
              {formatValue(detailData.inhaltFreigabeName)}
            </div>
            <div className={styles.label}>Finanzielle Freigabe*r:</div>
            <div className={styles.value}>
              {formatValue(detailData.finanzFreigabeName)}
            </div>
            <div className={styles.label}>Controlling:</div>
            <div className={styles.value}>
              {formatValue(detailData.controllingName)}
            </div>
            <div className={styles.label}>Studiengangsleitung:</div>
            <div className={styles.value}>
              {formatValue(
                `${detailData.studiengangsleitungName}${detailData.interimistisch ? " (interimistisch)" : ""}`,
              )}
            </div>
          </div>
        </section>

        <Dokumente projektId={detailData.itemId} dokumente={dokumente} />
      </div>
    </>
  );

  const renderEditMode = () => (
    <>
      <div ref={panelTopRef}>
        {errorMessage && (
          <div
            style={{
              fontSize: "16px",
              marginBottom: "30px",
              maxWidth: "500px",
              color: color,
            }}
          >
            {errorMessage}
          </div>
        )}
      </div>
      <div className="verticalLayout">
        <section className={styles.section}>
          <div className={styles.headerRow}>
            <h3 className={styles.sectionTitle}>Grunddaten</h3>
            <div style={{ display: "flex", gap: "8px" }}>
              {canEdit && (
                <Button
                  appearance="primary"
                  disabled={checkRequiredFields()}
                  onClick={() => {
                    _saveClicked();
                  }}
                >
                  Speichern
                </Button>
              )}
              <Button
                appearance="secondary"
                onClick={() => {
                  setErrorMessage("");
                  setEditMode(false);
                }}
              >
                Abbrechen
              </Button>
            </div>
          </div>
          <div className={styles.gridContainer}>
            <div className={styles.labelSingle} style={{ gridColumn: "1" }}>
              Projektname:
            </div>
            <div className={styles.valueSingle}>
              <Input
                className={styles.input}
                id="Projektname"
                value={projektname}
                autoFocus
                onChange={(e) =>
                  e.target.value.length <= 500 && setProjektname(e.target.value)
                }
              />
            </div>
            <div className={styles.valueWeiter}>
              <Checkbox
                label="Projekt wird nicht weiterverfolgt"
                checked={weiterverfolgung}
                onChange={(_, data) =>
                  setWeiterverfolgung(data.checked ? true : false)
                }
              />
            </div>
            <div className={styles.label}>Studiengangsbezeichnung:</div>
            <div className={styles.value}>
              <Input
                className={styles.input}
                id="Studiengangsbezeichnung"
                value={studiengangsbezeichnung}
                autoFocus
                onChange={(e) =>
                  e.target.value.length <= 500 &&
                  setStudiengangsbezeichnung(e.target.value)
                }
              />
            </div>
            <div className={styles.label}>Studiengangsart:</div>
            <div className={styles.value}>
              <Dropdown
                className={styles.input}
                id="Studiengangsart"
                value={getStudiengangsArtText(studiengangsArt)}
                autoFocus
                onOptionSelect={(_e, option) =>
                  option && setStudiengangsArt(option.optionValue ?? "")
                }
              >
                <Option value="2" text="Bachelor">
                  Bachelor
                </Option>
                <Option value="3" text="Master">
                  Master
                </Option>
                <Option value="4" text="Lehrgang">
                  Lehrgang
                </Option>
              </Dropdown>
            </div>
            <div className={styles.label}>Unterrichtssprache:</div>
            <div className={styles.value}>
              <Dropdown
                className={styles.input}
                id="Unterrichtssprache"
                value={unterrichtssprache}
                autoFocus
                onOptionSelect={(_e, option) =>
                  option && setUnterrichtssprache(option.optionValue ?? "")
                }
              >
                <Option value="Deutsch">Deutsch</Option>
                <Option value="Englisch">Englisch</Option>
              </Dropdown>
            </div>
            <div className={styles.label}>Institution:</div>
            <div className={styles.value}>
              <Input
                className={styles.input}
                id="Institution"
                value={institution}
                autoFocus
                onChange={(e) =>
                  e.target.value.length <= 500 && setInstitution(e.target.value)
                }
              />
            </div>
            <div className={styles.label}>
              Akademischer Grad (inkl. Abkürzung):
            </div>
            <div className={styles.value}>
              <Input
                className={styles.input}
                id="AkademischerGrad"
                value={akademischerGrad}
                autoFocus
                onChange={(e) =>
                  e.target.value.length <= 50 &&
                  setAkademischerGrad(e.target.value)
                }
              />
            </div>
            <div className={styles.label}>Studienfach:</div>
            <div className={styles.value}>
              <Input
                className={styles.input}
                id="Studienfach"
                value={studienfach}
                autoFocus
                onChange={(e) =>
                  e.target.value.length <= 500 && setStudienfach(e.target.value)
                }
              />
            </div>
            <div className={styles.label}>Studiengangsleitung:</div>
            <div className={styles.value}>
              {formatValue(detailData.studiengangsleitungName)}
            </div>
            <div className={styles.label}>Studiengangsstart:</div>
            <div className={styles.value}>
              <DatePicker
                className={styles.input}
                id="Studiengangsstart"
                value={studiengangsstart}
                autoFocus
                required
                formatDate={formatDate}
                onSelectDate={(date) => date && setStudiengangsstart(date)}
              />
            </div>
            <div className={styles.label}>Anzahl der Studienplätze:</div>
            <div className={styles.value}>
              <Input
                className={styles.input}
                id="Studienplaetze"
                type="number"
                min="0"
                max="500"
                value={anzahlStudienplaetze.toString()}
                autoFocus
                onChange={(e) =>
                  setAnzahlStudienplaetze(Number(e.target.value))
                }
              />
            </div>
            <div className={styles.label}>Organisationsstruktur:</div>
            <div className={styles.value}>
              <Input
                className={styles.input}
                id="Organisationsstruktur"
                value={organisationsstruktur}
                autoFocus
                onChange={(e) => setOrganisationsstruktur(e.target.value)}
              />
            </div>
            <div className={styles.label}>Programmstandort:</div>
            <div className={styles.value}>
              <Input
                className={styles.input}
                id="Programmstandort"
                value={standort}
                autoFocus
                onChange={(e) =>
                  e.target.value.length <= 500 && setStandort(e.target.value)
                }
              />
            </div>
            <div className={styles.label}>Gesamtumfang in ECTS-Punkten:</div>
            <div className={styles.value}>
              <Input
                className={styles.input}
                id="Gesamtumfang"
                value={gesamtumfang.toString()}
                autoFocus
                type="number"
                min={0}
                max={500}
                onChange={(e) =>
                  e.target.value !== "" &&
                  setGesamtumfang(Number(e.target.value))
                }
              />
            </div>
            <div className={styles.label}>Qualifikationsniveau:</div>
            <div className={styles.value}>
              <Input
                className={styles.input}
                id="Qualifikationsniveau"
                value={qualifikationsniveau}
                autoFocus
                onChange={(e) => setQualifikationsniveau(e.target.value)}
              />
            </div>
            <div className={styles.label}>Studiendauer in Semestern:</div>
            <div className={styles.value}>
              <Dropdown
                className={styles.input}
                id="Studiendauer"
                value={studiendauer.toString()}
                autoFocus
                onOptionSelect={(_e, option) =>
                  option && setStudiendauer(Number(option.optionValue))
                }
              >
                <Option value="0">0</Option>
                <Option value="1">1</Option>
                <Option value="2">2</Option>
                <Option value="3">3</Option>
                <Option value="4">4</Option>
                <Option value="5">5</Option>
                <Option value="6">6</Option>
                <Option value="7">7</Option>
                <Option value="8">8</Option>
                <Option value="9">9</Option>
                <Option value="10">10</Option>
              </Dropdown>
            </div>
            <div className={styles.labelSingle}>Zulassungsvoraussetzungen:</div>
            <div className={styles.valueSingleZusatz}>
              <Textarea
                className={styles.input}
                id="Zulassungsvoraussetzungen"
                value={zulassungsvoraussetzungen}
                autoFocus
                rows={2}
                onChange={(e) => setZulassungsvoraussetzungen(e.target.value)}
              />
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.headerRow}>
            <h3 className={styles.sectionTitle}>Relevante Personen</h3>
          </div>
          <div className={styles.gridContainer}>
            <div className={styles.label}>Entwickler*in:</div>
            <div className={styles.value}>
              <Dropdown
                className={styles.input}
                placeholder="User wählen..."
                value={
                  personen.find((role) => role.value === selectedPerson)
                    ?.text || ""
                }
                selectedOptions={selectedPerson ? [selectedPerson] : []}
                onOptionSelect={(_, data) => {
                  setSelectedPerson(data.optionValue || "");
                }}
              >
                {personen.map((role) => (
                  <Option key={role.value} value={role.value} text={role.text}>
                    {role.text}
                  </Option>
                ))}
              </Dropdown>
            </div>
            <div className={styles.label}>Freigeber*in:</div>
            <div className={styles.value}>
              <Dropdown
                className={styles.input}
                placeholder="User wählen..."
                value={
                  freigeber.find(
                    (role) => role.value === selectedPersonFreigeber,
                  )?.text || ""
                }
                selectedOptions={
                  selectedPersonFreigeber ? [selectedPersonFreigeber] : []
                }
                onOptionSelect={(_, data) => {
                  setSelectedPersonFreigeber(data.optionValue || "");
                }}
              >
                {freigeber.map((role) => (
                  <Option key={role.value} value={role.value} text={role.text}>
                    {role.text}
                  </Option>
                ))}
              </Dropdown>
            </div>
            <div className={styles.label}>Inhaltliche Freigabe*r:</div>
            <div className={styles.value}>
              <Dropdown
                className={styles.input}
                placeholder="User wählen..."
                value={
                  inhalt.find((role) => role.value === selectedPersonInhalt)
                    ?.text || ""
                }
                selectedOptions={
                  selectedPersonInhalt ? [selectedPersonInhalt] : []
                }
                onOptionSelect={(_, data) => {
                  setSelectedPersonInhalt(data.optionValue || "");
                }}
              >
                {inhalt.map((role) => (
                  <Option key={role.value} value={role.value} text={role.text}>
                    {role.text}
                  </Option>
                ))}
              </Dropdown>
            </div>
            <div className={styles.label}>Finanzielle Freigabe*r:</div>
            <div className={styles.value}>
              <Dropdown
                className={styles.input}
                placeholder="User wählen..."
                value={
                  finanz.find((role) => role.value === selectedPersonFinanz)
                    ?.text || ""
                }
                selectedOptions={
                  selectedPersonFinanz ? [selectedPersonFinanz] : []
                }
                onOptionSelect={(_, data) => {
                  setSelectedPersonFinanz(data.optionValue || "");
                }}
              >
                {finanz.map((role) => (
                  <Option key={role.value} value={role.value} text={role.text}>
                    {role.text}
                  </Option>
                ))}
              </Dropdown>
            </div>
            <div className={styles.label}>Controlling:</div>
            <div className={styles.value}>
              <Dropdown
                className={styles.input}
                placeholder="User wählen..."
                value={
                  controlling.find(
                    (role) => role.value === selectedPersonControlling,
                  )?.text || ""
                }
                selectedOptions={
                  selectedPersonControlling ? [selectedPersonControlling] : []
                }
                onOptionSelect={(_, data) => {
                  setSelectedPersonControlling(data.optionValue || "");
                }}
              >
                {controlling.map((role) => (
                  <Option key={role.value} value={role.value} text={role.text}>
                    {role.text}
                  </Option>
                ))}
              </Dropdown>
            </div>
            <div className={styles.label}>Studiengangsleitung:</div>
            <div className={styles.value}>
              <Dropdown
                className={styles.input}
                placeholder="User wählen..."
                value={
                  studiengangsleitung.find(
                    (role) => role.value === selectedPersonStg,
                  )?.text || ""
                }
                selectedOptions={selectedPersonStg ? [selectedPersonStg] : []}
                onOptionSelect={(_, data) => {
                  setSelectedPersonStg(data.optionValue || "");
                }}
              >
                {studiengangsleitung.map((role) => (
                  <Option key={role.value} value={role.value} text={role.text}>
                    {role.text}
                  </Option>
                ))}
              </Dropdown>
            </div>
            <div className={styles.valueWeiter}>
              <Checkbox
                label="Interimistisch"
                checked={interimistisch}
                onChange={(_, data) => setInterimistisch(data.checked === true)}
              />
            </div>
          </div>
        </section>

        <Dokumente
          projektId={detailData.itemId}
          dokumente={dokumente}
          editable={canEdit}
          onDokumenteUpdated={onDokumenteUpdated}
        />
      </div>
      <Dialog
        open={isDialogOpen}
        onOpenChange={(_e, data) => setIsDialogOpen(data.open)}
      >
        <DialogSurface>
          <DialogTitle style={{ marginBottom: "10px" }}>
            Änderungsbegründung
          </DialogTitle>
          <DialogContent>
            <div className={styles.label}>Begründung der Änderung:</div>
            <div className={styles.value}>
              <Textarea
                className={styles.input}
                id="bemerkung"
                value={updateBemerkung}
                autoFocus
                rows={3}
                onChange={(e) => setUpdateBemerkung(e.target.value)}
              />
            </div>
          </DialogContent>
          <DialogActions style={{ marginTop: "10px" }}>
            <Button
              appearance="primary"
              disabled={!updateBemerkung}
              onClick={() => {
                setIsDialogOpen(false);
                void saveProjekt();
              }}
            >
              Speichern
            </Button>
            <Button
              appearance="secondary"
              onClick={() => setIsDialogOpen(false)}
            >
              Abbrechen
            </Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>
    </>
  );

  return editMode ? renderEditMode() : renderReadMode();
};

export default Grunddaten;
